
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Textarea } from '@/components/ui/textarea';
import { AssessmentAnswers } from '@/types/assessment';
import { categoryExplanations } from '@/data/AIDigitalTransformationData';
import { categoryExplanations as learningCategoryExplanations, pillarConfig } from '@/data/AITrainingTransformationData';
import { useImageProxy } from '@/hooks/useImageProxy';

interface Question {
  id: number;
  question: string;
  responses: string[];
  category: string;
  type?: string;
}

interface AssessmentQuestionsProps {
  title: string;
  questions: Question[];
  answers: AssessmentAnswers;
  onAnswerChange: (questionId: number, answer: string | string[]) => void;
  categoryExplanation?: string;
  assessmentType?: 'ia-diagnostic' | 'training-transformation';
}

export default function AssessmentQuestions({
  title,
  questions,
  answers,
  onAnswerChange,
  categoryExplanation,
  assessmentType = 'ia-diagnostic'
}: AssessmentQuestionsProps) {
  const { getImageUrl, getAltText } = useImageProxy();
  
  // Pillar config for learning-strategy
  const pillar = assessmentType === 'training-transformation' && pillarConfig[title] ? pillarConfig[title] : null;
  const titleColor = pillar ? pillar.color : '#D7398B';

  // Log de debugging pour vérifier les données
  console.log(`🔍 [ASSESSMENT QUESTIONS DEBUG] Category: ${title}`, {
    questionsCount: questions.length,
    assessmentType,
    answersCount: Object.keys(answers).length,
    questions: questions.map(q => ({ id: q.id, responsesCount: q.responses.length }))
  });

  // Questions à choix multiples UNIQUEMENT dans l'audit transformation (questions 25 et 32)
  const multipleChoiceQuestions = assessmentType === 'ia-diagnostic' ? [25, 32] : [];

  // Utiliser l'explication fournie ou chercher dans categoryExplanations selon le type d'audit
  let explanation = categoryExplanation;
  if (!explanation) {
    if (assessmentType === 'training-transformation') {
      explanation = learningCategoryExplanations[title as keyof typeof learningCategoryExplanations];
    } else {
      explanation = categoryExplanations[title as keyof typeof categoryExplanations];
    }
  }

  // Fonction pour gérer les questions avec logique conditionnelle (Q25)
  const handleMultiSelectChange = (questionId: number, selectedValues: string[]) => {
    console.log(`🔄 [ASSESSMENT] Multi-select change for Q${questionId}:`, selectedValues);
    
    if (questionId === 25) { // Question "Avez-vous déjà utilisé des outils IA ?"
      const currentAnswer = Array.isArray(answers[questionId]) ? answers[questionId] as string[] : [];
      
      // Si "Aucun" ou "Ne sais pas" est sélectionné
      if (selectedValues.includes("Aucun") || selectedValues.includes("Ne sais pas")) {
        const exclusiveValue = selectedValues.includes("Aucun") ? "Aucun" : "Ne sais pas";
        
        // Si cette valeur exclusive était déjà sélectionnée seule, on la désélectionne
        if (currentAnswer.length === 1 && currentAnswer.includes(exclusiveValue)) {
          onAnswerChange(questionId, []);
        } else {
          // Sinon, on garde uniquement cette valeur exclusive
          onAnswerChange(questionId, [exclusiveValue]);
        }
      } else {
        // Pour les autres valeurs, comportement normal (sans "Aucun" ni "Ne sais pas")
        onAnswerChange(questionId, selectedValues);
      }
    } else {
      // Pour les autres questions à choix multiples, comportement normal
      onAnswerChange(questionId, selectedValues);
    }
  };

  return (
    <div className="w-full max-w-full overflow-hidden">
      <div className="text-center px-2 md:px-4">
        {pillar && assessmentType !== 'training-transformation' && (
          <div className="flex justify-center mb-3">
            <div 
              className="rounded-full p-3 flex items-center justify-center"
              style={{ backgroundColor: pillar.color }}
            >
              <img 
                src={getImageUrl(pillar.image)} 
                alt={getAltText(pillar.image)}
                className="h-6 w-6 object-contain"
              />
            </div>
          </div>
        )}
        <h2 className="text-xl md:text-2xl font-bold mb-4" style={{ color: titleColor }}>
          {title}
        </h2>
        {explanation && (
          <div 
            className="mx-auto max-w-full md:max-w-3xl mb-6 px-3 md:px-4 py-3 rounded-lg text-black italic text-sm md:text-base"
            style={{ backgroundColor: 'rgba(255, 102, 0, 0.1)' }}
          >
            {explanation}
          </div>
        )}
      </div>

      <div className="space-y-4 md:space-y-6 px-2 md:px-0">
        {questions.map((question) => {
          console.log(`🔍 [ASSESSMENT] Rendering question ${question.id}:`, {
            responsesCount: question.responses.length,
            responses: question.responses,
            isMultiSelect: multipleChoiceQuestions.includes(question.id)
          });

          return (
            <Card key={question.id} className="border-2 border-gray-100 w-full max-w-full">
              <CardContent className="p-4 md:p-6">
                <div className="space-y-3 md:space-y-4">
                  <h3 className="font-semibold text-base md:text-lg text-gray-800 break-words">
                    {question.question}
                  </h3>
                  
                  {question.responses.includes("Réponse libre") ? (
                    // Question à réponse libre
                    <Textarea
                      value={answers[question.id] as string || ''}
                      onChange={(e) => {
                        console.log(`🔄 [ASSESSMENT] Textarea change for Q${question.id}:`, e.target.value);
                        onAnswerChange(question.id, e.target.value);
                      }}
                      placeholder="Votre réponse..."
                      className="w-full border-2 border-gray-200 focus:border-[#D7398B] rounded-lg text-sm md:text-base"
                      rows={3}
                    />
                  ) : multipleChoiceQuestions.includes(question.id) ? (
                    // Questions à choix multiples avec MultiSelect (Q25 et Q32) - largeur fixe pour éviter le décalage
                    <div className="w-full relative" style={{ zIndex: 1 }}>
                      <MultiSelect
                        options={question.responses.map(response => ({
                          label: response,
                          value: response
                        }))}
                        value={Array.isArray(answers[question.id]) ? answers[question.id] as string[] : []}
                        onChange={(selectedValues) => handleMultiSelectChange(question.id, selectedValues)}
                        placeholder="Sélectionnez une ou plusieurs réponses"
                        className="w-full"
                        fixedWidth={true}
                      />
                    </div>
                  ) : (
                    // Toutes les autres questions : Select simple avec coins arrondis (comportement inchangé)
                    <div className="w-full relative" style={{ zIndex: 1 }}>
                      <Select
                        value={answers[question.id] as string || ''}
                        onValueChange={(value) => {
                          console.log(`🔄 [ASSESSMENT] Select change for Q${question.id}:`, value);
                          onAnswerChange(question.id, value);
                        }}
                      >
                        <SelectTrigger className="w-full border-2 border-gray-200 focus:border-[#D7398B] data-[state=open]:border-[#D7398B] rounded-full text-sm md:text-base">
                          <SelectValue placeholder="Sélectionnez une réponse" />
                        </SelectTrigger>
                        <SelectContent className="rounded-3xl bg-white border-2 border-gray-200 shadow-lg max-w-[90vw] w-full">
                          {question.responses.map((response, index) => {
                            console.log(`🔍 [ASSESSMENT] Rendering select option:`, response);
                            return (
                              <SelectItem key={index} value={response} className="cursor-pointer hover:bg-gray-50 text-sm md:text-base break-words">
                                {response}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
